import { XIcon } from "@heroicons/react/outline";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { patchData } from "../backend";
import { WorkflowContext } from "../contexts";
import useUser from "../hooks/useUser";
import { DocumentType } from "../interfaces";
import { BLOCK_ICON_MAP } from "../utils";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";

const SUPPORTED_DOCUMENT_TYPES: Array<DocumentType> = ["GOOGLE_DOCS", "GOOGLE_SLIDES"];

function WorkflowAiEnrichment() {
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const { t } = useTranslation();
  const { user, hasPermission } = useUser();
  const { workflow, mutateWorkflow } = useContext(WorkflowContext);

  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();

  const [documentIds, setDocumentIds] = useState<string[]>(workflow.aiEnrichment?.documentIds ?? []);
  const [hasChanges, setHasChanges] = useState(false);

  const canEdit = hasPermission(workflow.team, "workflows.edit");

  function handleCheckOnChange(value: boolean, documentId: string) {
    setHasChanges(true);

    if (value)
      setDocumentIds(ids => [...ids, documentId]);
    else
      setDocumentIds(ids => ids.filter(id => id !== documentId));
  }

  async function handleSave() {
    setIsSaving(true);

    await patchData(`/workflows/ai-enrichments/${workflow.aiEnrichment!.id}/`, { documentIds })
      .then(() => {
        mutateWorkflow();
        setIsSaving(false);
        setHasChanges(false);
        setIsSaved(true);

        clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setIsSaved(false), 2000);
      })
      .catch((err) => {
        console.error(err);
        setIsSaving(false);
      });
  }

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/ai-enrichment") && !!workflow.aiEnrichment && !!user?.subscription?.featureSalesAgent}>
      <TransitionWorkflowPageContent className="relative">
        <XIcon className="absolute top-4 right-4 w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
        <div className="flex flex-col w-[800px] h-full mx-auto mt-10">
          <div className="font-gilroy font-semibold text-3xl mb-4">
            {t("AI Enrichment")}
          </div>
          <div className="text-xs text-gray-600 font-semibold mb-1">
            {t("Select the templates to generate document suggestions for")}
          </div>
          <div className="flex flex-col overflow-y-auto gap-2 py-2 border-b border-t w-full" style={{ maxHeight: "calc(100vh - 400px)" }}>
            {workflow.documents.length === 0 &&
              <div className="text-sm text-gray-400">
                {t("Add a document to this workflow to generate suggestions.")}
              </div>
            }
            {workflow.documents.map((document) => {
              const isSupported = SUPPORTED_DOCUMENT_TYPES.includes(document.documentType);

              return (
                <label key={document.id} className={`flex items-center gap-2 text-sm ${isSupported ? "cursor-pointer" : "opacity-50"}`}>
                  <input type="checkbox" className="h-4 w-4 flex-shrink-0" checked={isSupported && documentIds.includes(document.id)} onChange={(e) => handleCheckOnChange(e.target.checked, document.id)} disabled={!isSupported || !canEdit} />
                  <img src={BLOCK_ICON_MAP[document.documentType.startsWith("GOOGLE_SHEETS") ? "GOOGLE_SHEETS" : document.documentType]} alt="File icon" className="h-8 w-8 flex-shrink-0" />
                  <div className="flex flex-col text-xs overflow-hidden">
                    <p className="font-gilroy font-bold whitespace-nowrap overflow-hidden text-ellipsis text-gray-600">
                      {document.file?.name ?? t("Untitled document")}
                    </p>
                    {!isSupported &&
                      <p className="text-gray-600 font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
                        {t("Suggestions are not supported for this document type")}
                      </p>
                    }
                  </div>
                </label>
              );
            })}
          </div>
          {canEdit &&
            <button className={`btn text-sm transition-colors w-fit mt-4 ${isSaved ? "btn-green" : "btn-blue"}`} onClick={handleSave} type="button" disabled={isSaving || !hasChanges}>{isSaving ? t("Saving...") : isSaved ? t("Saved") : t("Save")}</button>
          }
        </div>
      </TransitionWorkflowPageContent>
    </TransitionWorkflowPage>
  );
}

export default WorkflowAiEnrichment;
